import { Box, Card, Group, Stack } from '@mantine/core';
import { IconBolt, IconPlug, IconTimeline, IconWallet } from '@tabler/icons-react';
import { GetEvChargingSessionStatsResponse } from 'api/actions/get-ev-charging-session-stats/get-ev-charging-session-stats-response';
import useLang from 'lang';
import theme from 'theme';
import P2Medium from 'theme/components/Typography/P2Medium';
import P4Regular from 'theme/components/Typography/P4Regular';
import React from 'react';

interface UsageOverviewCardProps {
  stats: GetEvChargingSessionStatsResponse;
}

interface StatTileProps {
  icon: React.ComponentType<{ size?: number | string; color?: string }>;
  value: string;
  label: string;
}

/**
 * A single icon + value + label tile used in the usage overview grid.
 */
function StatTile({ icon: Icon, value, label }: StatTileProps) {
  return (
    <Stack align="center" gap={8}>
      <Box
        bg="gray.2"
        style={{
          borderRadius: 10,
          width: 40,
          height: 40,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={24} color={theme.colors?.gray?.[11]} />
      </Box>
      <Stack gap={0} align="center">
        <P2Medium ta="center">{value}</P2Medium>
        <P4Regular color="gray.9" ta="center">
          {label}
        </P4Regular>
      </Stack>
    </Stack>
  );
}

/**
 * "Your overview" card: all-time totals for the current customer at this
 * station - energy consumed, number of charges, total payments, and the
 * average charge size.
 */
export default function UsageOverviewCard({ stats }: UsageOverviewCardProps) {
  const { _t, lang } = useLang();

  /** Formats a number of kWh for display. */
  const formatKwh = (value: number) => `${value.toLocaleString(lang, { maximumFractionDigits: 1 })} kWh`;
  /** Formats a euro amount for display. */
  const formatEur = (value: number) =>
    `${value.toLocaleString(lang, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;

  return (
    <Stack gap={8}>
      <Group>
        <P4Regular color="gray.7">{_t('Your overview')}</P4Regular>
      </Group>
      <Card p={16} radius={10}>
        <Stack gap={16}>
          <Group wrap="nowrap" align="stretch" gap={16}>
            <Box style={{ flex: 1 }}>
              <StatTile icon={IconBolt} value={formatKwh(stats.totalConsumptionKwh)} label={_t('energy consumed')} />
            </Box>
            <Box w={1} style={{ backgroundColor: theme.colors?.gray?.[2] }} />
            <Box style={{ flex: 1 }}>
              <StatTile icon={IconPlug} value={`${stats.sessionCount}x`} label={_t('number of charges')} />
            </Box>
          </Group>
          <Box style={{ borderTop: `1px solid ${theme.colors?.gray?.[2]}` }} />
          <Group wrap="nowrap" align="stretch" gap={16}>
            <Box style={{ flex: 1 }}>
              <StatTile
                icon={IconWallet}
                value={
                  stats.totalPhillipBillablePriceWithVat == null
                    ? '—'
                    : formatEur(stats.totalPhillipBillablePriceWithVat)
                }
                label={_t('Phillip charges')}
              />
            </Box>
            <Box w={1} style={{ backgroundColor: theme.colors?.gray?.[2] }} />
            <Box style={{ flex: 1 }}>
              <StatTile
                icon={IconTimeline}
                value={formatKwh(stats.averageConsumptionKwh)}
                label={_t('average charge')}
              />
            </Box>
          </Group>
        </Stack>
      </Card>
    </Stack>
  );
}
